import React from 'react';
import SanityBackgroundImage, {
    type SanityBackgroundImageProps,
} from '@fogia/core-web/components/SanityMedia/SanityBackgroundImage';
import SanityBackgroundVideo, {
    type SanityBackgroundVideoProps,
} from '@fogia/core-web/components/SanityMedia/SanityBackgroundVideo';

/**
 * Represents a single media item (image or video) from Sanity.
 * Types are defined in fogia package, but we need them here for the component.
 *
 * @interface MediaItemComponent
 */
export interface MediaItemComponent {
    type: 'mediaItemComponent';
    mediaType?: 'image' | 'video';
    image?: {
        type: 'image';
        asset?: {
            ref: string;
            type: 'reference';
        };
        crop?: {
            bottom?: number;
            left?: number;
            right?: number;
            top?: number;
        };
        hotspot?: {
            height?: number;
            width?: number;
            x?: number;
            y?: number;
        };
        alt?: string;
    };
    video?: {
        file?: {
            type: 'file';
            asset?: {
                ref: string;
                type: 'reference';
            };
        };
        poster?: {
            type: 'image';
            asset?: {
                ref: string;
                type: 'reference';
            };
        };
    };
}

/**
 * Props for the SanityBackgroundMedia component
 *
 * @interface SanityBackgroundMediaProps
 */
export interface SanityBackgroundMediaProps
    extends Omit<SanityBackgroundImageProps, 'image'>,
        Omit<SanityBackgroundVideoProps, 'video'> {
    /** Media item component with type and image/video data */
    media: MediaItemComponent;
    /** Alt text for images (ignored for videos) */
    alt?: string;
    /** CSS object-fit value. Defaults to 'cover' */
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    /** Loading strategy. 'lazy' is default */
    loading?: 'lazy' | 'eager' | 'auto';
    /** Widths (px) for Sanity CDN image srcSet. Defaults to SANITY_IMAGE_SRCSET_WIDTHS when omitted. Not used for video. */
    srcSetWidths?: readonly number[];
    /** sizes attribute for <img>. Not used for video. */
    sizes?: string;
    /** Optional width for the media element (layout). */
    width?: string | number;
    /** Optional height for the media element (layout). */
    height?: string | number;
    /** React children to render on top of the media */
    children?: React.ReactNode;
}

export type SanityBackgroundMediaPropsMedia = Exclude<
    keyof SanityBackgroundMediaProps,
    'children' | 'alt' | 'objectFit' | 'loading' | 'srcSetWidths' | 'sizes' | 'width' | 'height'
>;

/**
 * Wrapper component that automatically renders either a Sanity image or video based on the media type.
 *
 * This component acts as a smart wrapper that delegates to either SanityBackgroundImage or
 * SanityBackgroundVideo based on the `mediaType` property in the media object. It provides a unified
 * API for handling both image and video backgrounds.
 *
 * @example
 * ```tsx
 * <SanityBackgroundMedia
 *   media={mediaItemComponent}
 *   alt=""
 *   objectFit="cover"
 *   loading="lazy"
 * >
 *   <h1>Content over media</h1>
 * </SanityBackgroundMedia>
 * ```
 *
 * @returns Rendered image or video component, or null if media is invalid
 */
const SanityBackgroundMedia = ({
    media,
    alt,
    objectFit = 'cover',
    loading = 'lazy',
    srcSetWidths,
    sizes,
    width,
    height,
    children,
    ...rest
}: SanityBackgroundMediaProps) => {
    if (!media || !media.mediaType) {
        return null;
    }

    if (media.mediaType === 'image' && media.image) {
        return (
            <SanityBackgroundImage
                image={media.image}
                alt={alt}
                objectFit={objectFit}
                loading={loading}
                srcSetWidths={srcSetWidths}
                sizes={sizes}
                width={width}
                height={height}
                {...rest}
            >
                {children}
            </SanityBackgroundImage>
        );
    }

    if (media.mediaType === 'video' && media.video) {
        return (
            <SanityBackgroundVideo
                video={media.video}
                objectFit={objectFit}
                loading={loading}
                width={width}
                height={height}
                {...rest}
            >
                {children}
            </SanityBackgroundVideo>
        );
    }

    return null;
};

export default SanityBackgroundMedia;
